import { HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ClientService } from '@celerofinancas/ui-http-services';
import { TokenResponse } from '@celerofinancas/ui-oauth';
import { Observable, finalize, share } from 'rxjs';

/**
 * Trades the host portal's token for a session of the api-client.
 *
 * Mirrors `AuthService` in `serasa-cl-webcomponents`, which is the shape the
 * production flow already uses: the portal never hands us a session, only its own
 * token, and `POST /v2/auth/token_exchange` is what turns one into the other.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  /**
   * Celero HTTP client, which already prefixes `environment.apiUrl`.
   */
  private readonly client = inject(ClientService);

  /**
   * In-flight exchange, shared so concurrent callers reuse one request.
   */
  private inFlight: Observable<HttpResponse<TokenResponse>> | null = null;

  /**
   * Token the in-flight exchange is for.
   */
  private lastToken: string | null = null;

  /**
   * Exchanges the host token for a session.
   *
   * The de-duplication is not an optimisation, it is what keeps the session
   * coherent: every screen extends `TokenExchange`, so a page holding three custom
   * elements fires three exchanges with the same token at once. Each answer is a
   * **different** session (the mock signs a fresh `jti` per call), and the last one
   * to land wins — the earlier tokens are already in storage and in flight on other
   * requests. Sharing one request means one session.
   *
   * `finalize` clears the cache on completion **and** on error, so a failed exchange
   * does not poison the next attempt with the same token.
   *
   * @param token token issued by the host portal
   * @returns the session response, shared between concurrent callers
   */
  public tokenExchange(token: string): Observable<HttpResponse<TokenResponse>> {
    if (this.inFlight && this.lastToken === token) {
      return this.inFlight;
    }

    this.lastToken = token;
    this.inFlight = this.client.post<TokenResponse>('/v2/auth/token_exchange', { token }).pipe(
      finalize(() => {
        this.inFlight = null;
        this.lastToken = null;
      }),
      share(),
    );

    return this.inFlight;
  }
}
